import React from 'react';
import { useStandaloneAcl } from '../../../Services/acl.service';
import { useEffectAsync } from '../../../helpers';
import Loading, { LoadingSize } from '../../../Components/Loading';
import { useDispatch, useSelector } from 'react-redux';
import {
	selectAclIsLoaded,
	selectAclIsLoading,
	selectAclTree,
	setAclLoading,
	setAclTree,
} from '../../../store/reducers/aclSlice';
import useWorkplaceRole from '../../Workplace/Hooks/useWorkplaceRole';
import { useAppContext } from '../../../Services/AppContext';

interface AclContextProps {
	isAllowed: (resource: string, permission?: string) => boolean;
}

const AclContext = React.createContext<AclContextProps>({
	isAllowed: () => false,
});

interface AclContextProviderProps {
	children: React.ReactElement | React.ReactElement[];
	loadingSize?: LoadingSize;
	showLoading?: boolean;
}

const AclContextProvider: React.FC<AclContextProviderProps> = props => {
	const { loggedUserId, workplaceId } = useAppContext();
	const { children, loadingSize, showLoading = true } = props;
	const { loadAcl, isAllowed: standaloneIsAllowed } = useStandaloneAcl();
	const aclIsLoading = useSelector(selectAclIsLoading);
	const aclIsLoaded = useSelector(selectAclIsLoaded);
	const aclData = useSelector(selectAclTree);
	const {} = useWorkplaceRole({
		userId: loggedUserId,
		workplaceId,
	});
	const dispatch = useDispatch();

	const refreshAcl = async () => {
		if (!aclIsLoaded && !aclIsLoading) {
			dispatch(setAclLoading(true));
			const tmpAclData = await loadAcl();
			dispatch(setAclTree(tmpAclData));
		}
	};

	useEffectAsync(async () => {
		await refreshAcl();
	});

	useEffectAsync(async () => {
		await refreshAcl();
	}, [aclIsLoaded]);

	const isAllowed = (resource: string, permission?: string) =>
		standaloneIsAllowed(aclData, resource, permission);

	return (
		// @ts-ignore
		<AclContext.Provider value={{ isAllowed }}>
			{aclIsLoading && showLoading ? (
				<div className={'py-5'}>
					<Loading size={loadingSize} />
				</div>
			) : (
				children
			)}
		</AclContext.Provider>
	);
};

export const useAclContext = () => React.useContext(AclContext);

export default AclContextProvider;
